<template>
  <!--
    36協定アラート。

    **この画面は、上限に触れている人だけを並べる。**全員の勤務時間を
    見る画面ではない（それは月次集計と稼働状況分析）。触れている
    人が誰なのかが分からなければ、幹部は何も手を打てない。氏名を出す
    のはここだけで、通知の文面には出さない。

    「触れていない」と「判定できていない」を、必ず別の見た目にする。
    同じにすると、見ていないことが見えなくなる。
  -->
  <div class="preika-insight-page">
    <section class="preika-page-hero preika-page-hero--limit">
      <div>
        <p class="preika-page-hero__eyebrow">OVERTIME LIMIT</p>
        <h1>36協定アラート</h1>
        <p>
          時間外・休日労働の上限に触れている方を、強い順に並べます。
          年360時間・年720時間・限度超過の回数は、協定の対象期間で数えます。
        </p>
      </div>
      <div class="preika-page-controls">
        <label>
          <span>対象月</span>
          <input v-model="targetMonth" type="month" @change="search">
        </label>
        <button
          type="button"
          class="preika-btn preika-btn--primary"
          :disabled="loading"
          @click="search"
        >
          <CIcon name="cil-loop"/> 再判定
        </button>
      </div>
    </section>

    <div v-if="loading" class="preika-analytics-loading">
      <span class="preika-analytics-spinner"></span>
      上限の判定をしています
    </div>

    <div v-else-if="error" class="preika-analytics-error" role="alert">
      <div><strong>上限状況を取得できませんでした</strong><small>{{ error }}</small></div>
      <button type="button" @click="callList">再取得</button>
    </div>

    <template v-else>
      <!--
        **この画面を開いた人に、まず届いているかどうかを見せる。**

        幹部がここを開くのは、たいてい月1回のお知らせを受けてから。
        逆に言えば、お知らせが届いていなければ開かれない。通知先を1つも
        決めていない会社では、上限を超えた方が並んでいても誰も見に来ない
        まま月が過ぎる。**そのとき画面にもログにも何も出ない。**

        だから、届いていないことをいちばん上に置く。
      -->
      <div
        v-if="deliveryNotice"
        class="preika-delivery-notice"
        :class="'preika-delivery-notice--' + deliveryNotice.tone"
        role="status"
      >
        <CIcon name="cil-bell"/>
        <div>
          <strong>{{ deliveryNotice.headline }}</strong>
          <small>{{ deliveryNotice.detail }}</small>
        </div>
      </div>

      <!--
        人数の内訳。**0名でも4枚とも出す。**
        「判定できなかった 0名」が消えると、見ていないことが見えなくなる。
      -->
      <section class="preika-analytics-kpis preika-analytics-kpis--four">
        <article
          class="preika-analytics-kpi"
          :class="{ 'preika-analytics-kpi--danger': data.violationCount > 0 }"
        >
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-warning"/></span>
          <small>上限を超過</small>
          <strong>{{ data.violationCount }}名</strong>
          <p>労働基準法36条6項の上限を超えています</p>
        </article>
        <article
          class="preika-analytics-kpi"
          :class="{ 'preika-analytics-kpi--warning': data.warningCount > 0 }"
        >
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-clock"/></span>
          <!--
            **「特別条項の回数」と書かない。**この区分には、月45時間を
            一度も超えていないのに年360時間を超えた方も入る。回数の話だと
            読むと、年720時間へ近づいていることに気づけない。
          -->
          <small>限度時間を超過（特別条項の範囲）</small>
          <strong>{{ data.warningCount }}名</strong>
          <p>月45時間または年360時間を超えていますが、特別条項の範囲です</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-bell"/></span>
          <!--
            **この人数はほかの区分と重なる。**上限を超えている方も、同じ月に
            80時間を超えていればここに入る。重ならない数え方にすると、
            いちばん重い方が通知義務の人数から消える（安衛則52条の2第3項は
            80時間を超えた労働者全員への通知を義務づけている）。
          -->
          <!--
            **「通知が必要 8名」だけだと、8名へ送ったと読まれる。**
            PREIKA から本人へ送る仕組みはまだ無い（安衛則52条の2第3項の
            本人通知）。ここで送信済みと読まれると、会社は義務を果たした
            つもりで止まる。**未送信であることを、人数と同じ場所に置く。**
          -->
          <small>本人への通知が必要（未送信）</small>
          <strong>{{ data.obligationCount }}名</strong>
          <p>月80時間を超えた方への通知義務があります（上の区分と重なります）</p>
          <p class="overtime-limit-not-sent">
            <strong>{{ productName }} からご本人へは、まだ送信していません。</strong>
            会社からご本人へ、超えた時間をお伝えください。
            管理者向けの Slack 通知は、ご本人への通知の代わりにはなりません。
          </p>
        </article>
        <!--
          **判定できなかった人は、0名の枠とは別の色にする。**
          触れていないのではなく、見えていない。
        -->
        <article
          class="preika-analytics-kpi"
          :class="{ 'preika-analytics-kpi--unknown': data.unknownCount > 0 }"
        >
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-magnifying-glass"/></span>
          <small>判定できなかった</small>
          <strong>{{ data.unknownCount }}名</strong>
          <!--
            **「集計値が読めない」だけではない。**36協定が登録されていない・
            期間が終わっている会社の方も、協定の上限については判定できない。
            ただし単月100時間・2〜6か月平均80時間・80時間超の本人通知は
            協定と関係なく判定するので、そちらに触れている方は上の区分へ入る。
          -->
          <p>協定または勤務時間の集計が確認できず、判定できていません</p>
        </article>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div>
            <p>AGREEMENT PERIOD</p>
            <h2>{{ displayMonth }}時点の判定</h2>
          </div>
          <router-link to="/admin/total/list" class="preika-text-link">
            月次集計を開く<CIcon name="cil-arrow-right"/>
          </router-link>
        </header>

        <!--
          いつの・どの期間の話かを、必ず画面に出す。
          出さないと「4月から数え直した数字」を暦年のものと読まれる。
        -->
        <dl class="preika-limit-facts">
          <div>
            <dt>対象期間</dt>
            <dd>{{ displayPeriod }}</dd>
          </div>
          <div>
            <dt>適用している協定</dt>
            <dd>{{ displayAgreement }}</dd>
          </div>
          <div>
            <dt>算定した時刻</dt>
            <dd>{{ data.evaluatedAt || '—' }}</dd>
          </div>
        </dl>

        <!--
          該当者0名でも、いつ算定したかを必ず出す。
          「何も出ない画面」は、壊れているのと区別が付かない。
        -->
        <div v-if="!rows.length" class="preika-analytics-empty">
          <CIcon name="cil-check-circle"/>
          <strong>{{ emptyHeadline }}</strong>
          <p>{{ emptyDescription }}</p>
        </div>

        <template v-else>
          <p class="preika-limit-count">
            上限に触れている方 {{ data.totalCount }}名中
            {{ firstIndex }}〜{{ lastIndex }}名を表示しています
          </p>

          <div class="preika-table-scroll">
            <table class="preika-data-table preika-limit-table">
              <thead>
                <tr>
                  <th scope="col">区分</th>
                  <th scope="col">社員番号</th>
                  <th scope="col">氏名</th>
                  <th scope="col">部署</th>
                  <!--
                    **2つに分ける。**「協定が確認できません」と
                    「1か月100時間に達しています」は同時に出る。
                    片方がもう片方を隠さないよう、列そのものを分ける。
                  -->
                  <th scope="col">法令で定められた上限・本人への通知</th>
                  <th scope="col">36協定で定めた上限</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.userId">
                  <td>
                    <span class="preika-limit-badge" :class="'is-' + levelKey(row.level)">
                      {{ levelLabel(row.level) }}
                    </span>
                  </td>
                  <td>{{ row.employeeId || '—' }}</td>
                  <td>{{ row.name || '—' }}</td>
                  <td>{{ row.departmentName || '未所属' }}</td>
                  <!--
                    1か月100時間未満・2〜6か月平均80時間以内・80時間超の
                    本人通知。**36協定の内容にかかわらず守るもの**なので、
                    協定が確認できない方にも出る。ここが協定の欄と一緒に
                    消えていたため、いちばん重い方が
                    「本人への通知が必要 0名」に隠れていた。
                  -->
                  <td>
                    <ul class="preika-limit-reasons">
                      <li
                        v-for="(message, index) in absoluteMessagesOf(row)"
                        :key="index"
                      >{{ message }}</li>
                      <li
                        v-if="!absoluteMessagesOf(row).length
                          && !notJudgedMessagesOf(row).length"
                        class="preika-limit-reasons__none"
                      >触れていません</li>
                    </ul>
                    <!--
                      **「触れていません」と同じ場所に置かない。**
                      超えていないのではなく、見ていない。
                    -->
                    <ul
                      v-if="notJudgedMessagesOf(row).length"
                      class="preika-limit-reasons preika-limit-reasons--not-judged"
                    >
                      <li
                        v-for="(message, index) in notJudgedMessagesOf(row)"
                        :key="index"
                      >{{ message }}</li>
                    </ul>
                  </td>
                  <!--
                    月の限度時間・年の限度時間・特別条項・限度超過の回数。
                    **協定が確認できないときは、その理由をここに出す。**
                  -->
                  <td>
                    <span
                      v-if="agreementNoticeOf(row)"
                      class="preika-limit-badge preika-limit-badge--agreement"
                      :class="agreementClassesOf(row)"
                    >{{ agreementNoticeOf(row).badge }}</span>
                    <ul class="preika-limit-reasons">
                      <li
                        v-for="(message, index) in agreementMessagesOf(row)"
                        :key="index"
                      >{{ message }}</li>
                      <li
                        v-if="agreementNoticeOf(row)"
                        class="preika-limit-reasons__action"
                      >{{ agreementNoticeOf(row).action }}</li>
                      <li
                        v-else-if="!agreementMessagesOf(row).length"
                        class="preika-limit-reasons__none"
                      >触れていません</li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-center">
            <!-- ページング処理 -->
            <VPagination
              v-model="currentPage"
              :click-handler="changePage"
              :page-count="totalPages"
              :force-page="currentPage"
              :force-ellipses="true"
              :container-class="'pagination'"
              :page-class="'page-item'"
              :prev-text="'<'"
              :next-text="'>'"
              :prev-link-class="'page-link'"
              :next-link-class="'page-link'"
              :page-link-class="'page-link'"
              :class-prefix="'pagination'"
            />
          </div>
        </template>
      </section>
    </template>
  </div>
</template>

<script>
import { PRODUCT_NAME } from '../../common/productName'
import axios from 'axios'
import moment from 'moment'
import VPagination from 'vuejs-paginate'
import {
  overtimeAgreementStatusClasses,
  overtimeAgreementStatusNotice
} from '../../utils/overtimeAgreementStatus'
import {
  defaultOvertimeLimitMonth,
  overtimeAlertDeliveryNotice,
  overtimeLimitConditionParams,
  overtimeLimitLevelKey,
  overtimeLimitLevelLabel
} from '../../utils/overtimeLimitStatus'
import { safeErrorDigest } from '../../utils/safeResponseMessage'

/*
 * 控えの鍵は **文字列をそのまま書く。** 定数に逃がさない。
 *
 * 「鍵が画面ごとに固有か」を見ている検査
 * （tests/unit/common/pagingIsTruthful.spec.js）は
 * `sessionStorage.setItem('…'` の literal を拾う。定数を渡すと
 * **この画面だけ検査の外に出て、鍵がかぶっても緑のまま通る。**
 * 実際に、定数にしていたときは他の画面と同じ鍵へ変えても落ちなかった。
 */

export default {
  name: 'OvertimeLimitStatus',

  components: {
    VPagination
  },

  data () {
    return {
      targetMonth: defaultOvertimeLimitMonth(),
      loading: false,
      error: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      data: {
        targetMonth: '',
        periodFromMonth: '',
        periodToMonth: '',
        agreementEffectiveFrom: '',
        evaluatedAt: '',
        violationCount: 0,
        warningCount: 0,
        obligationCount: 0,
        unknownCount: 0,
        totalCount: 0,
        companyAgreementStatus: '',
        companyAgreementStatusMessage: '',
        featureEnabled: false,
        featureDisabledMessage: '',
        rows: [],
        /*
         * 月1回のお知らせが、いま本当に届くか。
         * **既定は「届く」に倒さない。**古いサーバや取得の失敗で
         * 空が来たときに「届いている」と見せると、いちばん出したい
         * 状態だけが黙って消える。
         */
        notificationBlocker: '',
        notificationUsingSharedChannel: false
      }
    }
  },

  computed: {
    rows () {
      return Array.isArray(this.data.rows) ? this.data.rows : []
    },

    /**
     * 届いていないことの知らせ。届いていれば null（黙る）。
     *
     * 言葉は utils に置いてある。自社設定の画面でも同じ状態を出すので、
     * 片方だけ直すとずれる。
     */
    deliveryNotice () {
      return overtimeAlertDeliveryNotice(
        this.data.notificationBlocker,
        this.data.notificationUsingSharedChannel,
        this.data.notificationLastFailedMonth
      )
    },

    displayMonth () {
      const target = moment(this.targetMonth, 'YYYY-MM', true)
      return target.isValid() ? target.format('YYYY年M月') : this.targetMonth
    },

    displayPeriod () {
      const from = this.monthLabel(this.data.periodFromMonth)
      const to = this.monthLabel(this.data.periodToMonth)
      if (!from || !to) return '—'
      return from + ' 〜 ' + to
    },

    /**
     * どの協定で判定したか。
     *
     * 施行日が空のときは「協定が1本も記録されていない」という意味で、
     * 法定の原則値で判定している。**空欄にしない。**読んだ人が
     * 「自社の協定で判定された」と取り違える。
     */
    displayAgreement () {
      const effectiveFrom = this.data.agreementEffectiveFrom
      return effectiveFrom
        ? effectiveFrom + ' から適用の協定'
        : '協定の記録がないため、法令の原則値で判定しています'
    },

    /*
     * 「触れている人はいない」と「まだ判定できていない」を書き分ける。
     * どちらも一覧は空になるが、意味は正反対。
     */
    /**
     * <b>「問題なし」と読ませてよいのは、判定して何も無かったときだけ。</b>
     *
     * <p>一覧が空になる理由は3つある。判定していない（準備中）、
     * 36協定が登録されていない、判定して該当者が0名。どれも同じ
     * 見た目にすると、<b>幹部はいちばん軽い読み方をする。</b></p>
     */
    emptyHeadline () {
      if (!this.data.featureEnabled) {
        return '法令判定は準備中です'
      }
      if (this.companyAgreementNotice) {
        return '36協定の登録を確認してください'
      }
      return this.data.unknownCount > 0
        ? '判定できなかった方がいます'
        : '上限に触れている方はいません'
    },

    emptyDescription () {
      if (!this.data.featureEnabled) {
        return this.data.featureDisabledMessage
          || '労働時間の法令判定は準備中です。この画面ではまだ判定結果を出していません。'
      }
      const evaluatedAt = this.data.evaluatedAt
        ? this.data.evaluatedAt + ' に算定しました。'
        : ''
      if (this.companyAgreementNotice) {
        /*
         * **対象者0名を「問題なし」にしない。**
         *
         * 36協定を1本も登録していない会社で、たまたま上限に触れた人が
         * 0名だっただけの月に「上限に触れている方はいません」と出すと、
         * 幹部は協定そのものが無いことに気づかないまま次の月へ進む。
         */
        return evaluatedAt + this.companyAgreementNotice
      }
      return this.data.unknownCount > 0
        ? evaluatedAt + '月次集計が確定していない方がいます。勤務表の締めを確認してください。'
        : evaluatedAt + '対象期間の実績を見て、上限に触れている方はいませんでした。'
    },


    /** 製品の名前。**画面へ直に書かない。** */
    productName () {
      return PRODUCT_NAME
    },

    /** 会社ぜんぶの36協定の状態。判定できているなら空。 */
    companyAgreementNotice () {
      return String(this.data.companyAgreementStatusMessage || '')
    },

    firstIndex () {
      return this.rows.length
        ? (this.currentPage - 1) * this.pageSize + 1
        : 0
    },

    lastIndex () {
      return (this.currentPage - 1) * this.pageSize + this.rows.length
    }
  },

  mounted () {
    this.restore()
    this.callList()
  },

  methods: {
    /**
     * 判定していない範囲。空なら全部判定した。
     *
     * <p><b>「触れていません」と同じ見た目にしない。</b>ここが空でない方は、
     * 超えていないのではなく<b>見ていない</b>。</p>
     */
    notJudgedMessagesOf (row) {
      return row && Array.isArray(row.notJudgedMessages)
        ? row.notJudgedMessages
        : []
    },

    monthLabel (value) {
      const target = moment(String(value || ''), 'YYYY-MM', true)
      return target.isValid() ? target.format('YYYY年M月') : ''
    },

    /*
     * 区分の名前と寄せ方は utils に置く。**この画面で書き直さない。**
     * ダッシュボードのカードと言葉がずれると、同じ人が別の区分に
     * 見えることになる。
     */
    levelKey: overtimeLimitLevelKey,
    levelLabel: overtimeLimitLevelLabel,

    /**
     * 法令で定められた上限（1か月100時間未満・2〜6か月平均80時間以内）と、
     * 80時間超の本人通知。
     *
     * **36協定の内容にかかわらず守るもの**なので、協定が確認できない方にも
     * 出る。古いサーバが分けて返さないときは、まとめた文言をここへ置く
     * ——**空にすると、いちばん重い指摘が画面から消える。**
     */
    absoluteMessagesOf (row) {
      const source = row || {}
      if (Array.isArray(source.absoluteMessages) &&
          source.absoluteMessages.length) {
        return source.absoluteMessages
      }
      if (Array.isArray(source.agreementMessages) &&
          source.agreementMessages.length) {
        // 分けて返っている。こちらは本当に空。
        return []
      }
      return Array.isArray(source.messages) ? source.messages : []
    },

    /** 36協定で定めた上限（月・年・特別条項・限度超過の回数）の文言。 */
    agreementMessagesOf (row) {
      const source = row || {}
      return Array.isArray(source.agreementMessages)
        ? source.agreementMessages
        : []
    },

    /**
     * 協定を判定できているか。できていなければ、その理由と直し方。
     *
     * **判定できているときは null（何も出さない）。**「判定できています」を
     * 全員の行に出すと、本当に確認が要る行が埋もれる。
     */
    agreementNoticeOf (row) {
      const status = String((row || {}).agreementStatus || '')
      if (!status || status === 'valid') return null
      return overtimeAgreementStatusNotice(status)
    },

    agreementClassesOf (row) {
      return overtimeAgreementStatusClasses(
        this.agreementNoticeOf(row), 'preika-limit-agreement')
    },

    /**
     * 対象月を変えたときの入口。
     *
     * ページを1へ戻してから引き直す。5ページ目のまま月を変えると、
     * 結果が1ページしか無いときに空の表になる。
     */
    search () {
      this.currentPage = 1
      return this.callList()
    },

    changePage (page) {
      this.currentPage = Number(page) || 1
      return this.callList()
    },

    /**
     * 一覧と件数へ、**同じ絞り込みを送る。**
     *
     * 片方だけに送ると「件数はあるのに一覧が空」あるいはその逆になり、
     * ページ送りが壊れる。だから条件は1か所で作って両方へ渡す。
     */
    conditionParams () {
      return overtimeLimitConditionParams(this.targetMonth)
    },

    callList () {
      const condition = this.conditionParams()
      if (!condition) return Promise.resolve()

      this.loading = true
      this.error = ''

      return this.fetchTotalPages(condition)
        .then(() => axios.get(
          process.env.VUE_APP_API_ORIGIN + '/management/overtime-limit-status',
          {
            params: {
              ...condition,
              currentPage: this.currentPage,
              pageSize: this.pageSize
            }
          }
        ))
        .then(response => {
          this.data = this.normalize(response.data)
          this.save()
        })
        .catch(error => {
          this.error = error.response && error.response.status === 403
            ? 'この画面を表示する権限がありません。社内の管理者にご相談ください。'
            : '通信または判定の処理でエラーが発生しました。時間をおいて再度お試しください。'
          console.error('36協定アラートの取得に失敗しました:', safeErrorDigest(error))
        })
        .finally(() => { this.loading = false })
    },

    /**
     * ページ数。**最低1。**
     *
     * 0件のときに0にすると、ページャごと消えて前のページへ戻れなくなる。
     */
    fetchTotalPages (condition) {
      return axios
        .get(
          process.env.VUE_APP_API_ORIGIN + '/management/overtime-limit-status/count',
          { params: { ...condition } }
        )
        .then(response => {
          const count = Number(response.data) || 0
          this.totalPages = Math.max(1, Math.ceil(count / this.pageSize))
          if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages
          }
        })
        .catch(error => {
          this.totalPages = Math.max(1, this.totalPages)
          console.error('36協定アラートの件数取得に失敗しました:', safeErrorDigest(error))
        })
    },

    normalize (value) {
      const source = value || {}
      return {
        targetMonth: source.targetMonth || '',
        periodFromMonth: source.periodFromMonth || '',
        periodToMonth: source.periodToMonth || '',
        agreementEffectiveFrom: source.agreementEffectiveFrom || '',
        evaluatedAt: source.evaluatedAt || '',
        violationCount: Number(source.violationCount) || 0,
        warningCount: Number(source.warningCount) || 0,
        obligationCount: Number(source.obligationCount) || 0,
        unknownCount: Number(source.unknownCount) || 0,
        totalCount: Number(source.totalCount) || 0,
        /*
         * **会社ぜんぶの協定の状態は、行が0件でも来る。**
         * 行の有無と、協定が登録されているかは別の話。
         */
        companyAgreementStatus: source.companyAgreementStatus || '',
        companyAgreementStatusMessage:
          source.companyAgreementStatusMessage || '',
        /*
         * **既定は「出さない」へ倒す。**古いサーバや取得の失敗で
         * 空が来たときに「判定できています」と見せると、作りかけの
         * 数字が法令の判定として読まれる。
         */
        featureEnabled: source.featureEnabled === true,
        featureDisabledMessage: source.featureDisabledMessage || '',
        rows: Array.isArray(source.rows)
          ? source.rows.map(row => ({
            userId: row.userId,
            employeeId: row.employeeId || '',
            name: row.name || '',
            departmentName: row.departmentName || '',
            level: row.level || 'unknown',
            messages: Array.isArray(row.messages) ? row.messages : [],
            /*
             * **判定していない範囲は、区分にも先頭メッセージにも出ない。**
             *
             * 区分（level）はいちばん強い指摘で決まるので、違反が1件
             * 出ていると「年の累計は見ていない」が消える。back は別項目で
             * 返しているので、こちらも別に持って別に出す。
             */
            notJudgedMessages: Array.isArray(row.notJudgedMessages)
              ? row.notJudgedMessages
              : [],
            /*
             * **2つの系統を、分けたまま持つ。**
             *
             * まとめた1本にすると「協定が確認できません」の下へ
             * 「1か月100時間に達しています」が埋もれる。埋もれるのは
             * いちばん重い指摘で、しかも協定を結び直していない会社ほど
             * そこに当たりやすい。
             *
             * **既定は「判定できています」へ倒さない。**古いサーバや
             * 取得の失敗で空が来たときに「協定は確認できています」と
             * 見せると、いちばん出したい状態だけが黙って消える。
             */
            agreementStatus: row.agreementStatus || '',
            worktimeDataStatus: row.worktimeDataStatus || '',
            agreementMessages: Array.isArray(row.agreementMessages)
              ? row.agreementMessages
              : [],
            absoluteMessages: Array.isArray(row.absoluteMessages)
              ? row.absoluteMessages
              : [],
            notificationDuty: row.notificationDuty === true,
            absoluteViolation: row.absoluteViolation === true
          }))
          : [],
        /*
         * **空を「届いている」に読み替えない。**返ってこなかったときに
         * 黙るようにすると、いちばん出したい状態だけが画面から消える。
         * 分からないものは分からないまま持ち、utils 側で扱いを決める。
         */
        notificationBlocker: source.notificationBlocker || '',
        notificationUsingSharedChannel:
          source.notificationUsingSharedChannel === true,
        /*
         * 設定がそろっていても送信そのものは失敗する。**そのときだけ
         * 対象月が入る。**空は「失敗していない」で読んでよい（届いたか
         * どうかは上の blocker が持つ）。
         */
        notificationLastFailedMonth: source.notificationLastSendFailed === true
          ? (source.notificationLastFailedMonth || '')
          : ''
      }
    },

    /*
     * 控えは**この画面だけの鍵**へ置く。鍵を他の画面と分け合うと、
     * 片方で選んだ年月がもう片方へ漏れる（実際に月次集計と業務報告で
     * 起きている）。
     */
    save () {
      sessionStorage.setItem('overtimeLimitStatus-search-params', JSON.stringify({
        targetMonth: this.targetMonth,
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalPages: this.totalPages
      }))
    },

    restore () {
      const saved = sessionStorage.getItem('overtimeLimitStatus-search-params')
      if (saved) {
        const params = JSON.parse(saved)
        this.targetMonth = params.targetMonth || this.targetMonth
        this.currentPage = Number(params.currentPage) || 1
        this.pageSize = Number(params.pageSize) || this.pageSize
        this.totalPages = Math.max(1, Number(params.totalPages) || 1)
      }
    }
  }
}
</script>
